import { useGetAccount } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useFeatureFlag } from "configcat-react";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

// ---------------------------------------------------------------------------
// Experiment registry
// ---------------------------------------------------------------------------

export type ExperimentDefinition = {
    id: string;
    name: string;
    description: string;
};

/**
 * Registry of known experiments. Lifecycle stage and availability are
 * controlled entirely via ConfigCat targeting rules — no `stage` or
 * `availabilityFlag` fields here.
 */
export const KNOWN_EXPERIMENTS = {
    ralph: {
        id: "experiment_ralph",
        name: "Ralph Mode",
        description: "Run Ona in a loop to autonomously build software while you're AFK",
    },
} as const satisfies Record<string, ExperimentDefinition>;

export type ExperimentName = keyof typeof KNOWN_EXPERIMENTS;

const ALL_EXPERIMENT_NAMES = Object.keys(KNOWN_EXPERIMENTS) as ExperimentName[];

export function isKnownExperiment(name: string): name is ExperimentName {
    return name in KNOWN_EXPERIMENTS;
}

export function getExperiment(name: string): ExperimentDefinition | null {
    if (!isKnownExperiment(name)) {
        return null;
    }
    return KNOWN_EXPERIMENTS[name];
}

// ---------------------------------------------------------------------------
// localStorage helpers — all experiment keys are scoped by org ID
// ---------------------------------------------------------------------------

function experimentsEnabledKey(orgId: string): string {
    return `experiments_enabled:${orgId}`;
}

function createLocalStorageSubscription(key: string) {
    return (callback: () => void) => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === key) {
                callback();
            }
        };
        window.addEventListener("storage", handleStorageChange);
        const handleCustomEvent = () => callback();
        window.addEventListener(`localStorage:${key}`, handleCustomEvent);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener(`localStorage:${key}`, handleCustomEvent);
        };
    };
}

function readLocalStorageBool(key: string, defaultValue: boolean): boolean {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) return defaultValue;
        return raw === "true";
    } catch {
        return defaultValue;
    }
}

function writeLocalStorage(key: string, value: string) {
    try {
        localStorage.setItem(key, value);
        window.dispatchEvent(new Event(`localStorage:${key}`));
    } catch (e) {
        console.warn(`Failed to write localStorage key ${key}`, e);
    }
}

// ---------------------------------------------------------------------------
// useOrgId — shared helper to get the current org ID for localStorage scoping
// ---------------------------------------------------------------------------

function useOrgId(): string {
    const { data: user } = useAuthenticatedUser();
    return user?.organizationId ?? "";
}

// ---------------------------------------------------------------------------
// useExperimentsEnabled — global toggle (per-org)
// ---------------------------------------------------------------------------

/**
 * GLOBAL TOGGLE (opt-out) — experiments are enabled by default.
 *
 * Read/write the org-scoped `experiments_enabled` flag. Defaults to true
 * so eligible users get experiments without an explicit opt-in step.
 * Users can opt out via Settings, which writes `false`. Stored in
 * User Preferences API (cross-device) and localStorage (immediate reads).
 */
export function useExperimentsEnabled(): {
    enabled: boolean;
    setEnabled: (v: boolean) => void;
    loading: boolean;
} {
    const orgId = useOrgId();
    const localStorageKey = experimentsEnabledKey(orgId);
    const { value: remoteValue, setValue: setRemoteValue, isLoading } = usePreferenceValue(localStorageKey);

    const subscribe = useMemo(() => createLocalStorageSubscription(localStorageKey), [localStorageKey]);
    const localEnabled = useSyncExternalStore(
        subscribe,
        () => readLocalStorageBool(localStorageKey, true),
        () => true,
    );

    // Remote wins when defined (cross-device sync), otherwise fall back to local
    const enabled = remoteValue !== undefined ? remoteValue === "true" : localEnabled;

    const setEnabled = useCallback(
        (v: boolean) => {
            if (!orgId) return; // prevent writes to unscoped keys before user data loads
            writeLocalStorage(localStorageKey, String(v));
            setRemoteValue(String(v));
        },
        [orgId, localStorageKey, setRemoteValue],
    );

    return useMemo(() => ({ enabled, setEnabled, loading: isLoading }), [enabled, setEnabled, isLoading]);
}

// ---------------------------------------------------------------------------
// useExperimentEnabled — primary hook for components
// ---------------------------------------------------------------------------

/**
 * Returns whether an experiment is active for the current user and org.
 * Passes user state as ConfigCat custom attributes so targeting rules
 * evaluate everything (global toggle, org/tier) in one flag lookup.
 */
export function useExperimentEnabled(name: ExperimentName): boolean {
    const experiment = KNOWN_EXPERIMENTS[name];
    const { enabled: experimentsEnabled } = useExperimentsEnabled();
    const { data: account } = useGetAccount();
    const { data: organization } = useOrganization();

    const accountId = account?.id ?? "";
    const accountEmail = account?.email ?? "";
    const orgId = organization?.id ?? "";
    const orgTier = OrganizationTier[organization?.tier ?? OrganizationTier.UNSPECIFIED];

    const user = useMemo(
        () => ({
            identifier: accountId,
            email: accountEmail,
            custom: {
                organization_id: orgId,
                organization_tier: orgTier,
                experiments_enabled: String(experimentsEnabled),
            },
        }),
        [accountId, accountEmail, orgId, orgTier, experimentsEnabled],
    );

    const { value } = useFeatureFlag(experiment.id, false, user);

    return value;
}

// ---------------------------------------------------------------------------
// useExperimentVisibleInSettings — Settings UI visibility probe
// ---------------------------------------------------------------------------

/**
 * Probes ConfigCat to determine if the current user is in the target audience
 * for an experiment, assuming experiments are enabled and not opted out.
 * Used by the Settings page to decide which experiments to show, and to
 * determine whether the experiments Settings page should be accessible at all.
 *
 * Returns `loading: true` while account/org data is still being fetched so
 * callers can show a skeleton instead of flashing <NotAuthorized />.
 */
export function useExperimentVisibleInSettings(name: ExperimentName): {
    visible: boolean;
    loading: boolean;
} {
    const experiment = KNOWN_EXPERIMENTS[name];
    const { data: account, isLoading: accountLoading } = useGetAccount();
    const { data: organization, isLoading: orgLoading } = useOrganization();

    const accountId = account?.id ?? "";
    const accountEmail = account?.email ?? "";
    const orgId = organization?.id ?? "";
    const orgTier = OrganizationTier[organization?.tier ?? OrganizationTier.UNSPECIFIED];

    // Memoize the user object to avoid re-render loops — useFeatureFlag
    // triggers re-evaluation when the user object reference changes.
    const user = useMemo(
        () => ({
            identifier: accountId,
            email: accountEmail,
            custom: {
                organization_id: orgId,
                organization_tier: orgTier,
                experiments_enabled: "true",
            },
        }),
        [accountId, accountEmail, orgId, orgTier],
    );

    const { value } = useFeatureFlag(experiment.id, false, user);

    // Wait for account/org data before trusting the flag value — ConfigCat
    // evaluates with empty attributes while these are loading and returns false.
    const loading = accountLoading || orgLoading;

    return useMemo(() => ({ visible: loading ? false : value, loading }), [loading, value]);
}

/**
 * Returns true if any experiment is visible in Settings for the current user/org.
 * Used to decide whether to show the experiments Settings page and sidebar badge.
 */
export function useAnyExperimentVisibleInSettings(): {
    visible: boolean;
    loading: boolean;
} {
    // Call all visibility hooks unconditionally (React rules of hooks).
    // With a single experiment this is trivial; if more are added, each needs
    // its own hook call here.
    const ralph = useExperimentVisibleInSettings("ralph");
    return useMemo(() => ({ visible: ralph.visible, loading: ralph.loading }), [ralph.visible, ralph.loading]);
}

// ---------------------------------------------------------------------------
// getEnabledExperimentsMap — synchronous, for Segment enrichment
// ---------------------------------------------------------------------------

/**
 * Synchronous read of experiment states from localStorage for a given org.
 * Returns a map like `{ ralph: true }` for Segment enrichment.
 * All known experiments are enabled when the global toggle is on (default).
 */
export function getEnabledExperimentsMap(orgId: string | undefined): Record<string, boolean> {
    if (!orgId) return {};

    const globalEnabled = readLocalStorageBool(experimentsEnabledKey(orgId), true);
    if (!globalEnabled) {
        return {};
    }

    return Object.fromEntries(ALL_EXPERIMENT_NAMES.map((name) => [name, true]));
}

// ---------------------------------------------------------------------------
// Migration — one-time, from legacy feature_flag_* keys
// ---------------------------------------------------------------------------

const MIGRATION_FLAG = "experiments_migration_v1";
const LEGACY_RALPH_KEY = "feature_flag_ralph_mode_enabled";

/**
 * One-time migration from the legacy experiment system.
 * Cleans up the old `feature_flag_ralph_mode_enabled` localStorage key.
 * With the opt-out default, no explicit write to `experiments_enabled` is
 * needed — experiments are enabled by default for eligible users.
 */
export function migrateExperimentState(): void {
    try {
        if (localStorage.getItem(MIGRATION_FLAG)) {
            return;
        }

        localStorage.removeItem(LEGACY_RALPH_KEY);
        localStorage.setItem(MIGRATION_FLAG, "true");
    } catch (e) {
        console.warn("Experiment migration failed", e);
    }
}

/**
 * Hook that runs the one-time experiment migration on mount.
 * Call this from a component that renders on every authenticated page load.
 */
export function useExperimentMigration(): void {
    useEffect(() => {
        migrateExperimentState();
    }, []);
}

// ---------------------------------------------------------------------------
// Pending experiment activation (for /experiments/:name URL route)
// ---------------------------------------------------------------------------

const PENDING_EXPERIMENT_KEY = "experiment_pending_activation";

/**
 * Store a pending experiment activation to be processed after login/onboarding.
 * The URL route captures the intent; after auth, the global toggle is enabled.
 */
export function setPendingExperiment(name: string) {
    if (isKnownExperiment(name)) {
        try {
            localStorage.setItem(PENDING_EXPERIMENT_KEY, name);
        } catch {
            // Ignore localStorage errors
        }
    }
}

export function getPendingExperiment(): string | null {
    try {
        return localStorage.getItem(PENDING_EXPERIMENT_KEY);
    } catch {
        return null;
    }
}

export function clearPendingExperiment() {
    try {
        localStorage.removeItem(PENDING_EXPERIMENT_KEY);
    } catch {
        // Ignore localStorage errors
    }
}
